import { useEffect } from 'react';

interface PageMetaProps {
  title: string;
  description: string;
  canonicalPath: string;
  robots?: string;
  keywords?: string[];
  ogType?: string;
}

const SITE_URL = 'https://skore.info';
const DEFAULT_OG_IMAGE = `${SITE_URL}/opengraph-image.png`;

function normalizeCanonicalPath(canonicalPath: string): string {
  if (!canonicalPath || canonicalPath === '/') {
    return '';
  }

  return canonicalPath.startsWith('/') ? canonicalPath : `/${canonicalPath}`;
}

function upsertMetaTag(nameOrProperty: string, content: string, isProperty = false) {
  const selector = isProperty
    ? `meta[property="${nameOrProperty}"]`
    : `meta[name="${nameOrProperty}"]`;
  let meta = document.querySelector(selector) as HTMLMetaElement | null;

  if (!meta) {
    meta = document.createElement('meta');
    if (isProperty) {
      meta.setAttribute('property', nameOrProperty);
    } else {
      meta.setAttribute('name', nameOrProperty);
    }
    document.head.appendChild(meta);
  }

  meta.setAttribute('content', content);
}

function removeMetaTag(nameOrProperty: string, isProperty = false) {
  const selector = isProperty
    ? `meta[property="${nameOrProperty}"]`
    : `meta[name="${nameOrProperty}"]`;

  document.querySelector(selector)?.remove();
}

const PageMeta: React.FC<PageMetaProps> = ({
  title,
  description,
  canonicalPath,
  robots = 'index, follow',
  keywords,
  ogType = 'website'
}) => {
  useEffect(() => {
    const normalizedCanonicalPath = normalizeCanonicalPath(canonicalPath);
    const canonicalUrl = `${SITE_URL}${normalizedCanonicalPath}`;

    document.title = title;

    upsertMetaTag('description', description);
    upsertMetaTag('robots', robots);

    if (keywords?.length) {
      upsertMetaTag('keywords', keywords.join(', '));
    } else {
      removeMetaTag('keywords');
    }

    upsertMetaTag('og:title', title, true);
    upsertMetaTag('og:description', description, true);
    upsertMetaTag('og:url', canonicalUrl, true);
    upsertMetaTag('og:type', ogType, true);
    upsertMetaTag('og:image', DEFAULT_OG_IMAGE, true);
    upsertMetaTag('og:site_name', 'skore.info', true);

    upsertMetaTag('twitter:card', 'summary_large_image');
    upsertMetaTag('twitter:title', title);
    upsertMetaTag('twitter:description', description);
    upsertMetaTag('twitter:image', DEFAULT_OG_IMAGE);

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);
  }, [canonicalPath, description, keywords, ogType, robots, title]);

  return null;
};

export default PageMeta;
